import { makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import { ShieldPersonFilled } from "@fluentui/react-icons";
import { useMemo } from "react";
import { $redStatus, $yellowStatus } from "../../../../cssjs/colors";
import { useApiResource } from "../../../../hooks/useApiResource";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../../utils/common/Constants";
import { extractPaxScreeningData } from "../../../../utils/pax/PaxSecurityHelper";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import OverviewCardHeader from "../../../shared/components/cards/OverviewCardHeader";
import PaxSecurityCharts from "./PaxSecurityCharts";

//Define the base URL and the Flights Table URL
const domain = process.env.REACT_APP_API_BASE_URL;
const paxScreeningEndPoint = process.env.REACT_APP_API_PAX_SCREENING;
const paxScreeningBaseUrl = `${domain}${paxScreeningEndPoint}`;

export interface IPaxSecurityProps {
  airport: string;
}

const PaxSecurity: React.FC<IPaxSecurityProps> = ({ airport }) => {
  const classes = useStyles();

  const paxScreeningUrl =
    paxScreeningBaseUrl.replace(AIRPORT_CODE_PARAMETER, airport) || "";

  const {
    data: paxScreeningData,
    isError,
    isLoading,
  } = useApiResource<any>({
    url: paxScreeningUrl,
    refetchOnWindowFocus: false,
    queryKey: ["pax-security-overview", airport],
    refetchInterval: 60000,
  });

  const {
    cardStatus,
    screeningLegendItems,
    screeningDonutData,
    capacityDonutData,
    totalScreeningTime,
  } = useMemo(
    () => extractPaxScreeningData(paxScreeningData),
    [paxScreeningData]
  );

  const noDatacard = (
    <div>
      <NoDataCard message={EMPTY_STATE_MESSAGE.Passenger} />
    </div>
  );

  return (
    <>
      {isError && noDatacard}
      {isLoading && <Spinner labelPosition="below" label="Loading..." />}
      {!isError && !isLoading && paxScreeningData && (
        <section
          className={mergeClasses(classes.cardContainer, cardStatus)}
          data-testid="pax-security-card"
        >
          <OverviewCardHeader
            icon={<ShieldPersonFilled />}
            title="Screening Time"
            displayValue={`${totalScreeningTime}m`}
          />
          <PaxSecurityCharts
            legendItems={screeningLegendItems}
            donuts={[
              {
                label: "Screening Time",
                resourceData: screeningDonutData,
              },
              {
                label: "Z2 Capacity",
                resourceData: capacityDonutData,
              },
            ]}
          />
        </section>
      )}
    </>
  );
};

export default PaxSecurity;

const useStyles = makeStyles({
  cardContainer: {
    padding: "8px",
    boxSizing: "border-box",
    "&.good": {
      backgroundColor: "#FFFFFF",
    },
    "&.moderate": {
      border: `3px solid ${$yellowStatus}`,
      backgroundColor: "#FBFAE6",
    },
    "&.poor": {
      border: `3px solid ${$redStatus}`,
      backgroundColor: "#fdf6f7",
    },
  },
});
